
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Lesson, Word } from '@/models/Lesson';
import { v4 as uuidv4 } from 'uuid';
import { toast } from '@/lib/toast';
import { Trash } from 'lucide-react';

interface LessonFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (lesson: Lesson) => void;
  editLesson?: Lesson;
}

const WordPairInput = ({ 
  index, 
  word, 
  translation, 
  onWordChange, 
  onTranslationChange,
  onRemove,
  showRemove
}: { 
  index: number, 
  word: string, 
  translation: string, 
  onWordChange: (value: string) => void,
  onTranslationChange: (value: string) => void,
  onRemove: () => void,
  showRemove: boolean
}) => {
  return (
    <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center mb-2">
      <Input
        value={word}
        onChange={(e) => onWordChange(e.target.value)}
        placeholder={`French word ${index + 1}`}
      />
      <Input
        value={translation}
        onChange={(e) => onTranslationChange(e.target.value)}
        placeholder={`Translation ${index + 1}`}
      />
      {showRemove && (
        <Button 
          type="button" 
          variant="ghost" 
          size="icon" 
          onClick={onRemove}
          className="h-8 w-8 text-destructive"
        >
          <Trash className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
};

const LessonForm = ({ isOpen, onClose, onSave, editLesson }: LessonFormProps) => {
  const [name, setName] = useState('');
  const [active, setActive] = useState(true);
  const [wordPairs, setWordPairs] = useState<Array<{id: string, word: string, translation: string}>>([
    { id: uuidv4(), word: '', translation: '' },
    { id: uuidv4(), word: '', translation: '' },
    { id: uuidv4(), word: '', translation: '' },
    { id: uuidv4(), word: '', translation: '' },
    { id: uuidv4(), word: '', translation: '' }
  ]);
  
  useEffect(() => {
    if (isOpen) {
      if (editLesson) {
        setName(editLesson.name);
        setActive(editLesson.active !== undefined ? editLesson.active : true);
        
        const pairs = editLesson.words.map(word => ({
          id: word.id,
          word: word.word,
          translation: word.translation
        }));
        
        const filledPairs = [...pairs];
        while (filledPairs.length < 5) {
          filledPairs.push({ id: uuidv4(), word: '', translation: '' });
        }
        
        setWordPairs(filledPairs);
      } else {
        setName('');
        setActive(true);
        setWordPairs([
          { id: uuidv4(), word: '', translation: '' },
          { id: uuidv4(), word: '', translation: '' },
          { id: uuidv4(), word: '', translation: '' },
          { id: uuidv4(), word: '', translation: '' },
          { id: uuidv4(), word: '', translation: '' }
        ]);
      }
    }
  }, [isOpen, editLesson]);
  
  const handleWordChange = (id: string, value: string) => {
    setWordPairs(prev => prev.map(pair => 
      pair.id === id ? { ...pair, word: value } : pair
    ));
  };
  
  const handleTranslationChange = (id: string, value: string) => {
    setWordPairs(prev => prev.map(pair => 
      pair.id === id ? { ...pair, translation: value } : pair
    ));
  };
  
  const handleAddWordPair = () => {
    setWordPairs(prev => [...prev, { id: uuidv4(), word: '', translation: '' }]);
  };
  
  const handleRemoveWordPair = (id: string) => {
    setWordPairs(prev => prev.filter(pair => pair.id !== id));
  };
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim()) {
      toast.error('Please provide a name for the lesson');
      return;
    }
    
    const filledWordPairs = wordPairs.filter(
      pair => pair.word.trim() !== '' && pair.translation.trim() !== ''
    );
    
    if (filledWordPairs.length === 0) {
      toast.error('Please add at least one word pair');
      return;
    }
    
    const words: Word[] = filledWordPairs.map(pair => ({
      id: pair.id,
      word: pair.word.trim(),
      translation: pair.translation.trim(),
      category: 'lesson'
    }));
    
    const lessonData: Lesson = {
      id: editLesson?.id || uuidv4(),
      name: name.trim(),
      words,
      active,
      order: editLesson?.order || 0,
      createdAt: editLesson?.createdAt || new Date(),
      updatedAt: new Date()
    };
    
    onSave(lessonData);
    onClose();
    
    if (editLesson) {
      toast.success(`"${name}" updated successfully`);
    } else {
      toast.success(`"${name}" added to your lessons`);
    }
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editLesson ? 'Edit Lesson' : 'Add New Lesson'}
          </DialogTitle>
          <DialogDescription>
            {editLesson 
              ? 'Edit your vocabulary lesson details and word pairs.' 
              : 'Create a new vocabulary lesson with word pairs.'}
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="name">Lesson Name</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter lesson name"
            />
          </div>
          
          <div className="flex items-center space-x-2">
            <Switch
              id="active"
              checked={active}
              onCheckedChange={setActive}
            />
            <Label htmlFor="active">Active</Label>
          </div>
          
          <div className="space-y-2">
            <Label>Words</Label>
            <div className="border rounded-md p-3 space-y-2">
              {wordPairs.map((pair, index) => (
                <WordPairInput
                  key={pair.id}
                  index={index}
                  word={pair.word}
                  translation={pair.translation}
                  onWordChange={(value) => handleWordChange(pair.id, value)}
                  onTranslationChange={(value) => handleTranslationChange(pair.id, value)}
                  onRemove={() => handleRemoveWordPair(pair.id)}
                  showRemove={wordPairs.length > 1}
                />
              ))}
              
              <Button 
                type="button" 
                variant="outline" 
                size="sm" 
                onClick={handleAddWordPair}
                className="w-full mt-2"
              >
                Add More Words
              </Button>
            </div>
          </div>
          
          <DialogFooter className="pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">
              {editLesson ? 'Save Changes' : 'Add Lesson'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default LessonForm;
